import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Field, FieldCheckbox, FieldInput, FieldLabel } from '@ibe/ui';
import {
  ArrowRight,
  BarChart3,
  Building2,
  Headphones,
  Lock,
  Mail,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { ApiError, NetworkError } from '../lib/api-client.js';
import { AUTH_STATUS, useAuth } from './auth-context.js';

const schema = z.object({
  email: z.email('Enter a valid email address'),
  password: z.string().min(1, 'Enter your password'),
  rememberMe: z.boolean(),
});

type LoginForm = z.infer<typeof schema>;

interface LocationState {
  readonly from?: string;
}

/**
 * A marketing feature block shown on the left panel.
 */
function Feature({
  icon: Icon,
  title,
  description,
}: {
  readonly icon: LucideIcon;
  readonly title: string;
  readonly description: string;
}): React.JSX.Element {
  return (
    <div>
      <Icon className="h-6 w-6 text-primary" aria-hidden="true" />
      <p className="mt-2 text-sm font-semibold text-text">{title}</p>
      <p className="mt-0.5 text-xs text-text-muted">{description}</p>
    </div>
  );
}

/**
 * The sign-in page.
 *
 * One deliberate omission: the failure message never distinguishes an unknown
 * email from a wrong password. "No account with that address" is a working
 * account-enumeration oracle — an attacker with a list of addresses learns
 * which of your staff have logins, and that is the half of the credential
 * they cannot otherwise guess.
 *
 * A lockout, by contrast, is stated plainly. Telling someone their account is
 * temporarily locked reveals nothing they could not discover by trying, and
 * withholding it produces a support call instead of a wait.
 */
export function LoginPage(): React.JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const returnTo = (location.state as LocationState | null)?.from ?? '/dashboard';

  if (auth.status === AUTH_STATUS.AUTHENTICATED) {
    return <Navigate to={returnTo} replace />;
  }

  if (auth.status === AUTH_STATUS.MFA_ENROLL || auth.status === AUTH_STATUS.MFA_VERIFY) {
    return <Navigate to="/mfa" replace />;
  }

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await auth.login(values);
      void navigate(returnTo, { replace: true });
    } catch (error) {
      if (error instanceof NetworkError) {
        setSubmitError(error.message);
        return;
      }
      if (error instanceof ApiError) {
        setSubmitError(
          error.code === 'ACCOUNT_LOCKED'
            ? error.message
            : 'That email address and password do not match an account.',
        );
        return;
      }
      throw error;
    }
  });

  return (
    <main className="flex min-h-screen w-full">
      <div className="hidden w-1/2 flex-col justify-between bg-accent p-12 lg:flex">
        <div className="flex items-center gap-2">
          <Building2 className="h-6 w-6 text-primary" aria-hidden="true" />
          <span className="text-lg font-semibold text-text">IBE Admin Portal</span>
        </div>

        <div className="space-y-6">
          <div>
            <p className="text-sm font-medium text-primary">Welcome back!</p>
            <h1 className="mt-1 text-4xl font-bold leading-tight text-text">
              Sign in to your <span className="text-primary">admin account</span>
            </h1>
            <p className="mt-3 max-w-sm text-body text-text-muted">
              Manage your properties, reservations, and business operations in one place.
            </p>
          </div>

          <div className="flex items-center justify-center">
            <Building2 className="h-80 w-80 text-primary/10" aria-hidden="true" />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Feature
            icon={ShieldCheck}
            title="Secure Access"
            description="Your data is safe and protected."
          />
          <Feature
            icon={BarChart3}
            title="Smart Management"
            description="Manage everything effortlessly."
          />
          <Feature
            icon={Headphones}
            title="24/7 Support"
            description="We're here to help any time."
          />
        </div>
      </div>

      <div className="flex w-full flex-col items-center justify-center bg-background p-6 lg:w-1/2">
        <div className="w-full max-w-md space-y-6 rounded-2xl bg-surface p-8 shadow-card">
          <div className="text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent">
              <Building2 className="h-7 w-7 text-primary" aria-hidden="true" />
            </div>
            <h2 className="mt-4 text-2xl font-semibold text-text">Sign in</h2>
            <p className="mt-1 text-sm text-text-muted">Enter your credentials to continue</p>
          </div>

          {auth.endedReason === null ? null : <Alert tone="info">{auth.endedReason}</Alert>}

          <form onSubmit={(event) => void onSubmit(event)} noValidate className="space-y-4">
            {/* Above the fields, not above the button: someone who has just been
                refused needs to read why before they retype a password, and an
                error that appears between the last field and the submit control
                moves that control down as it renders. */}
            {submitError === null ? null : <Alert tone="error">{submitError}</Alert>}

            <Field error={errors.email?.message} required>
              <FieldLabel>Email address</FieldLabel>
              <div className="relative">
                <Mail
                  className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted"
                  aria-hidden="true"
                />
                <FieldInput
                  type="email"
                  autoComplete="username"
                  placeholder="Enter your email"
                  className="pl-10"
                  {...register('email')}
                />
              </div>
            </Field>

            <Field error={errors.password?.message} required>
              <FieldLabel>Password</FieldLabel>
              <div className="relative">
                <Lock
                  className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted"
                  aria-hidden="true"
                />
                <FieldInput
                  type="password"
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  className="pl-10"
                  {...register('password')}
                />
              </div>
            </Field>

            <div className="flex items-center justify-between gap-2">
              <Field>
                <Controller
                  control={control}
                  name="rememberMe"
                  render={({ field }) => (
                    <FieldCheckbox checked={field.value} onCheckedChange={field.onChange}>
                      Keep me signed in on this device
                    </FieldCheckbox>
                  )}
                />
              </Field>
              <Link to="/forgot-password" className="shrink-0 text-sm text-primary hover:underline">
                Forgot password?
              </Link>
            </div>

            <Button type="submit" loading={isSubmitting} className="w-full">
              Sign in
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </form>

          <div className="relative border-t">
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-surface px-2 text-sm text-text-muted">
              or
            </span>
          </div>

          <p className="text-center text-sm text-text-muted">
            Need help?{' '}
            <Link to="/support" className="text-primary hover:underline">
              Contact support
            </Link>
          </p>
        </div>
      </div>
    </main>
  );
}
